import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { ShipSearchResult } from '../Search/useShipSearch';

interface ShipSelectionContextValue {
  zoomToShip: AutoCompleteShipInfo | null;
  setZoomToShip: (
    newZoomToShip: AutoCompleteShipInfo | ShipSearchResult | null,
  ) => void;
  isClickingShip: boolean;
  setIsClickingShip: Dispatch<SetStateAction<boolean>>;
}

const ShipSelectionContext = createContext<
  ShipSelectionContextValue | undefined
>(undefined);

export const ShipSelectionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isClickingShip, setIsClickingShip] = useState<boolean>(false);
  const [zoomToShip, _setZoomToShip] = useState<AutoCompleteShipInfo | null>(
    null,
  );

  const setZoomToShip = useCallback(
    (newZoomToShip: AutoCompleteShipInfo | ShipSearchResult | null) => {
      if (!newZoomToShip) {
        _setZoomToShip(null);
        return;
      }

      const newZoomToShipAutoComplete = {
        callSign:
          'callSign' in newZoomToShip
            ? (newZoomToShip.callSign as string | null)
            : null,
        imo:
          newZoomToShip?.imo !== null && newZoomToShip?.imo !== undefined
            ? newZoomToShip.imo.toString()
            : null,
        mmsi:
          newZoomToShip?.mmsi !== null && newZoomToShip?.mmsi !== undefined
            ? newZoomToShip.mmsi.toString()
            : null,
        name: newZoomToShip?.name ? newZoomToShip.name : null,
        type:
          'type' in newZoomToShip
            ? (newZoomToShip.type as string | null)
            : null,
        filteredOut:
          'filteredOut' in newZoomToShip
            ? (newZoomToShip.filteredOut as boolean | undefined)
            : undefined,
        forceView:
          'forceView' in newZoomToShip
            ? (newZoomToShip.forceView as boolean | undefined)
            : undefined,
        source:
          'source' in newZoomToShip && newZoomToShip.source === 'remote'
            ? ('remote' as const)
            : undefined,
      };
      _setZoomToShip(newZoomToShipAutoComplete);
    },
    [],
  );

  return (
    <ShipSelectionContext.Provider
      value={{ zoomToShip, setZoomToShip, isClickingShip, setIsClickingShip }}
    >
      {children}
    </ShipSelectionContext.Provider>
  );
};

export const useShipSelection = () => {
  const context = useContext(ShipSelectionContext);
  if (!context) {
    throw new Error(
      'useShipSelection must be used within a ShipSelectionProvider',
    );
  }
  return context;
};
